<template>
  <v-card v-if="tenant">
    <v-card-title>
      <section-header icon="mdi mdi-credit-card-outline">
        {{ $t('tenant.payment.title') }}
      </section-header>
    </v-card-title>
    <v-card-text>
      <v-row>
        <v-col v-if="$currentTenantPlan === 'FREE'" class="text-center" cols="12">
          <v-btn-toggle v-model="interval" class="mb-2" mandatory>
            <v-btn value="month">{{ $t('tenant.payment.interval.monthly') }}</v-btn>
            <v-btn value="year">{{ $t('tenant.payment.interval.yearly') }}</v-btn>
          </v-btn-toggle>
          <v-chip class="ml-2" color="info" label>{{ $t('tenant.payment.save_up_to') }}</v-chip>
        </v-col>
        <!--<v-col class="text-center" cols="12">
          <v-btn color="primary" href="https://priorit.io/pricing" target="_blank" text>
            {{ $t('tenant.payment.learn_more_about_plans') }}
            <v-icon right>mdi mdi-arrow-right</v-icon>
          </v-btn>
        </v-col>-->
        <v-col v-for="plan in $plans" :key="plan.value" cols="12" md="4">
          <v-card
            :class="[plan.value === $currentTenantPlan && 'current-plan', plan.value !== 'FREE' && 'fill-height']"
            :disabled="$currentTenantPlan === 'TOTAL' && plan.value !== 'TOTAL'"
            class="d-flex flex-column justify-space-between"
            color="grey lighten-4"
            light
          >
            <v-avatar v-if="plan.value === $currentTenantPlan" class="current-plan-icon" color="white" size="18">
              <v-icon color="secondary" size="20">mdi mdi-check-circle</v-icon>
            </v-avatar>
            <v-card-title>
              <div>
                <span class="text-subtitle-2 font-weight-bold">{{ plan.name }}</span>
                <v-chip
                  v-if="plan.value === $currentTenantPlan && !canceledPlan"
                  :small="$vuetify.breakpoint.xlOnly"
                  :x-small="$vuetify.breakpoint.lgAndDown"
                  class="ml-1"
                  color="secondary"
                >{{ $t('tenant.payment.active') }}
                </v-chip>
                <v-chip
                  v-if="plan.value === $currentTenantPlan && canceledPlan"
                  :disabled="true"
                  :small="$vuetify.breakpoint.xlOnly"
                  :x-small="$vuetify.breakpoint.lgAndDown"
                  class="ml-1"
                >{{ $t('tenant.payment.canceled') }}
                </v-chip>
                <div v-if="pro" class="text-h5 text-xl-h4 mt-2 primary--text">
                  <div>
                    {{ prices[plan.value][currentInterval].monthly }}{{ pro[currentInterval].currencySymbol }}
                    <span class="text-body-1 grey--text text--lighten-1">/{{ $t('tenant.payment.month') }}</span>
                  </div>
                </div>
              </div>
              <v-spacer></v-spacer>
            </v-card-title>
            <v-card-text v-if="$currentTenantPlan === plan.value && plan.value === 'PRO'" class="caption grey--text">
              <span v-if="!canceledPlan">{{
                  $t('tenant.payment.next_duedate', {0: $formatDaysUntilDate(tenant.currentSubscriptionPeriodEnd)})
                }}</span>
              <span v-else
              >{{ $t('tenant.payment.will_expire_at', {0: $formatDaysUntilDate(tenant.currentSubscriptionPeriodEnd)}) }}
              </span>

              ({{ $formatDateNoTime(tenant.currentSubscriptionPeriodEnd) }}).
            </v-card-text>
            <v-card-actions>
              <v-btn
                v-if="plan.value === 'PRO'"
                :block="$vuetify.breakpoint.xsOnly"
                :disabled="(!!checkingOut || $currentTenantPlan === 'PRO') && !canceledPlan"
                :loading="checkingOut === 'pro'"
                :small="$vuetify.breakpoint.lgAndDown"
                color="secondary"
                @click="canceledPlan ? customerPortal() : select(pro[currentInterval])"
              >
                {{
                  canceledPlan
                    ? $t('tenant.payment.continue_plan')
                    : $currentTenantPlan === 'PRO'
                      ? $t('tenant.payment.current_plan')
                      : $t('tenant.payment.choose_plan')
                }}
              </v-btn>
              <v-btn
                v-if="plan.value === 'TOTAL'"
                :block="$vuetify.breakpoint.xsOnly"
                :disabled="$currentTenantPlan === 'TOTAL'"
                :small="$vuetify.breakpoint.lgAndDown"
                color="secondary"
                href="mailto:info@ekd.fi?subject=EKD.fi%20Portal%20TOTAL%20Plan%20Quote&body=Hi,%0A%0AI%20would%20like%20to%20request%20quote%20on%20EKD.fi%20Portal%20TOTAL%20plan.%0A%0A"
              >
                {{
                  $currentTenantPlan === 'TOTAL' ? $t('tenant.payment.current_plan') : $t('tenant.payment.contact_us')
                }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-card-text>
    <v-card-actions v-if="customer" :class="$vuetify.breakpoint.xsOnly ? 'flex-column' : ''">
      <v-btn
        v-if="$currentTenantPlan !== 'TOTAL'"
        :block="$vuetify.breakpoint.xsOnly"
        :class="$vuetify.breakpoint.xsOnly ? 'ma-1' : ''"
        :outlined="$vuetify.breakpoint.xsOnly"
        color="primary"
        text
        @click="customerPortal"
      >
        <v-icon v-if="$icons" left>edit</v-icon>
        {{ $t('tenants.payment.edit_payment_details') }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import stripeApi from '@/api/stripe';
import tenantApi from '@/api/tenant';
import SectionHeader from '@/components/SectionHeader';

export default {
  name: 'Payments',
  components: {SectionHeader},
  data() {
    return {
      interval: 'year',
      pro: null,
      checkingOut: null,
      customer: null,
      tenant: null,
    };
  },
  methods: {
    async customerPortal() {
      try {
        const portal = await stripeApi.customerPortal();
        if (portal.url) {
          window.location = portal.url;
        }
      } catch (error) {
        this.$handleApiError(error);
      }
    },
    async select(price) {
      this.checkingOut = 'pro';
      try {
        await stripeApi.checkout(price.id);
      } catch (error) {
        this.$handleApiError(error);
      }
      this.checkingOut = null;
    },
    async reloadTenant() {
      try {
        this.tenant = await tenantApi.getCurrentTenant();
      } catch (error) {
        this.$handleApiError(error);
      }
    },
    async reloadCustomer() {
      try {
        this.customer = await stripeApi.getCustomerInfo();
      } catch (error) {
        this.$handleApiError(error);
      }
    },
    async reloadPrices() {
      try {
        const proPrices = await stripeApi.getPrices();
        const pro = {
          month: proPrices.find(p => p.interval === 'month' && p.intervalCount === 1),
          year: proPrices.find(p => p.interval === 'year' && p.intervalCount === 1),
        };
        if (pro.month) {
          pro.month.monthly = pro.month.unitAmount / 100;
        }
        if (pro.year) {
          pro.year.monthly = pro.year.unitAmount / 12 / 100;
        }
        this.pro = pro;
      } catch (error) {
        this.$handleApiError(error);
      }
    },
  },
  computed: {
    canceledPlan() {
      return this.$isPaidTenant && this.customer?.cancelAt != null;
    },
    currentInterval() {
      if (this.customer && this.pro) {
        if (this.pro['month'].id === this.customer.priceId) {
          return 'month';
        } else if (this.pro['year'].id === this.customer.priceId) {
          return 'year';
        }
      }
      if (this.interval) {
        return this.interval;
      }
      return 'month';
    },
    prices() {
      return {
        FREE: {month: {monthly: '0'}, year: {monthly: '0'}},
        PRO: this.pro,
        TOTAL: {
          month: {monthly: `${this.$t('tenant.payment.starting')} 999`},
          year: {monthly: `${this.$t('tenant.payment.starting')} 999`},
        },
      };
    },
  },
  mounted() {
    this.reloadCustomer();
    this.reloadPrices();
    this.reloadTenant();
  },
};
</script>

<style lang="scss" scoped>
.current-plan {
  border: 1px solid var(--v-secondary-base) !important;
  position: relative;

  .current-plan-icon {
    position: absolute;
    right: -5px;
    top: -5px;
  }
}
</style>
